/**
 * OwalSearch.js
 *
 * @description Filter functions for the faces search UI.
 *
 * @author Moshe Berman
 * @copyright 2018, Owal Inc.
 *
 * Created on 02/13/2018.
 */

import { OwalSearchMatchCondition } from './OwalSearchMatchCondition'
import { OwalSearchSynonym } from './OwalSearchSynonym' // eslint-disable-line
import { objectAtKeypath } from '../util/objectAtKeypath'

/**
 * A class which executes searches on objects based on predicates.
 *
 * @export
 * @class OwalSearchController
 */
export class OwalSearchController {
  /**
   * Performs a search using an OwalSearchPredicate or OwalSearchCompoundPredicate.
   *
   * @param {String} query The search query to search with.
   * @param {OwalSearchPredicate} predicate The predicate or compound predicate to search against.
   * @param {Array<Object>} objects an array of objects to search.
   * @param {Array<OwalSearchSynonym>} synonyms An array of synonyms.
   * @memberof OwalSearchController
   */
  searchUsingPredicate (query, predicate, objects, synonyms = []) {
    // console.debug('predicate: ', predicate)

    let results = objects
    if (query) {
      const subqueries = this._subqueriesFromQuery(query)
      subqueries.forEach(subquery => {
        results = this._searchUsingPredicate(
          subquery,
          predicate,
          results,
          synonyms,
        )
        // console.log(`Subquery: ${JSON.stringify(subquery)} has ${results.length} results.`)
      })
    } else {
      results = this._searchUsingPredicate(undefined, predicate, objects)
    }
    return results
  }

  /** --- Sorting --- */

  /**
   * Sorts a list of objects using a sort predicate.
   *
   * @param {OwalSortPredicate} predicate The predicate to use to sort.
   * @param {Array} objects The objects to sort.
   * @memberof OwalSearchController
   */
  sortUsingPredicate (predicate, objects) {
    objects.sort(predicate.comparisonFunction)
  }

  /** --- Helpers ---- */

  /**
   * Breaks a search query down into further queries.
   *
   * @param {string} query A search query.
   * @returns An array of objects with a search term and a property.
   * @memberof OwalSearchController
   */
  _subqueriesFromQuery (query) {
    if (!query || !query.split) {
      return []
    }
    const queries = query.split(',').map(subquery => {
      const clause = subquery.split(':')

      const clauseHasKeyPrefix = clause.length === 2

      /** No prefix means all keys. */
      const implicitWildcard = clause.length === 1
      const explicitWildcard = clauseHasKeyPrefix && clause[0] === '*'
      const clauseIsWildcard = explicitWildcard || implicitWildcard

      const searchTerm = (clauseHasKeyPrefix ? clause[1] : clause[0]).trim()

      const q = {
        key: clauseIsWildcard ? undefined : clause[0].trim(),
        wildcard: clauseIsWildcard,
        searchTerm,
      }
      return q
    })
    return queries
  }

  /**
   * A predicate contains a search term, a match condition, and one or more keys to apply its match condition to.
   *
   * @param {any} predicate
   * @memberof OwalSearchController
   */
  _searchUsingPredicate (query, predicate, objects, synonyms) {
    let keys = query
      ? query.wildcard
        ? predicate.searchKeys
        : [query.key]
      : []
    const searchTerm = query ? query.searchTerm : undefined
    // console.debug(`=> Searching\n--> predicate: ${predicate}\n--> keys: ${keys}`)
    const matchCondition = predicate.matchCondition
    const caseSensitive = predicate.caseSensitive
    // console.debug(`Synonyms inside search: `, synonyms)
    let matchingObjects = objects.filter(object => {
      let match = true

      if (predicate.callback) {
        // console.debug(`=> Using callback: `, predicate.callback)
        match = predicate.callback(object)
      } else {
        if (!keys || keys.length === 0) {
          keys = Object.keys(object)
          // console.debug('No keys specified, all keys.')
        }
        // console.debug(`---> Keys: `, JSON.stringify(keys))
        // TODO: Synonym mapping here
        const mapped = keys.map(key => {
          return this._objectMatchesSearchTermForKey(
            object,
            searchTerm,
            key,
            matchCondition,
            caseSensitive,
            synonyms,
          )
        })

        match = mapped.includes(true)
      }

      // console.debug(`PREDICATE`, JSON.stringify(predicate, null, 2))
      // console.debug(`Filter object`, object, `matches`, match)
      return match
    })

    return matchingObjects
  }

  /**
   * Takes a search term and if it's a synonym of a known term, returns the root word.
   *
   * @param {any} query The string to normalize
   * @param {any} key The key we're searching
   * @param {any} synonyms A list of OwalSearchSynonyms.
   * @returns The original string, or a rootWord, if the original string is a synonym.
   * @memberof OwalSearchController
   */
  _searchTermForKeyWithSynonyms (query, key, synonyms) {
    let relevantSynonyms = synonyms.filter(synonym => synonym.appliesToKey(key))

    for (const synonym of relevantSynonyms) {
      const rootSynonym = synonym.rootWordForSynonym(query, true)
      if (rootSynonym !== query) {
        return rootSynonym
      }
    }

    // No synonyms match.
    return query
  }

  /**
   * Searches the object and returns the object at the keypath, if a match is found.
   *
   * @param {Object} object The object to search.
   * @param {String} keypath The keypath to search.
   * @returns
   * @memberof OwalSearchController
   */
  _findObjectAtKeypath (object, keypath) {
    return objectAtKeypath(object, keypath)
  }

  /**
   * Searches an object for a match, given a search term, a key to search, and match conditions.
   *
   * @param {any} object The object to search.
   * @param {any} searchTerm The term to search.
   * @param {any} key The property to search.
   * @param {any} matchCondition The condition under which a search is a match.
   * @param {any} caseSensitive Specifies if the search is case sensitive or not.
   * @memberof OwalSearchController
   */
  _objectMatchesSearchTermForKey (
    object,
    needle,
    key = '',
    matchCondition,
    caseSensitive,
    synonyms,
  ) {
    let haystack = this._findObjectAtKeypath(object, key)

    //    console.debug(`==> Object Matches...`, JSON.stringify(object))
    //    console.debug(`====> Needle: `, needle)
    //    console.debug(`====> Haystack: `, haystack)
    //    console.debug(`====> key: `, key)
    //    console.debug(`====> MatchCondition: `, matchCondition)

    if (!haystack) {
      return false
    }

    let valueType = typeof haystack
    // console.debug('valueType', valueType)
    // TODO: Handle numeric searches, object searches?
    if (valueType === 'string') {
      haystack = caseSensitive ? haystack : haystack.toLowerCase()
      needle = caseSensitive ? needle : needle.toLowerCase()
      // console.log('case sensitive: ', caseSensitive)
    } else if (valueType === 'boolean') {
      if (key === needle) {
        const searchTermIsKeyAndValueIsTrue = haystack
        console.debug(`searchtermiskey: `, searchTermIsKeyAndValueIsTrue)
        return searchTermIsKeyAndValueIsTrue
      }
    }

    const normalizedSearchTerm = this._searchTermForKeyWithSynonyms(
      needle,
      key,
      synonyms,
    )
    // console.debug(`search term: ${needle} and synonym: ${normalizedSearchTerm}`)
    const match = OwalSearchMatchCondition.implementation[matchCondition](
      haystack,
      normalizedSearchTerm,
    )
    // console.debug(`Object:`, object, 'includes: ', match)
    return match
  }
}
